import {
  Flex,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { useIntl } from "react-intl";
import { refusalOf } from "api/refusal";
import { refreshSession, withSessionRefresh } from "api/SessionAuth";
import { telegramOidcLink } from "api/TelegramAuth";
import BusyButton from "components/UIComponents/BusyButton";
import { NONCE_REFRESH_MS, useTelegramOidcPopup } from "hooks/useTelegramOidcPopup";
import { FOCUS_RING, TOUCH_TARGET } from "utils/chackra/controlStyles";
import { telegramLinkErrorId, telegramPopupErrorId } from "./linkErrors";

const MAX_SESSION_REFRESH_TICKS = 2;

interface TelegramLinkBodyProps {
  clientId: number;
  userId: number;
  isOpenNow: MutableRefObject<boolean>;
  isLinking: boolean;
  setIsLinking: (value: boolean) => void;
  /** The link call has answered: close, and catch the card up if `linked`. */
  finish: (linked: boolean) => void;
}

/**
 * Mounted only while the dialog is open, because the hook fetches a nonce on
 * mount. The session is kept fresh here as well: the popup cannot wait for an
 * await, and a user may read the warning for longer than the access cookie lives.
 */
const TelegramLinkBody = ({
  clientId,
  userId,
  isOpenNow,
  isLinking,
  setIsLinking,
  finish,
}: TelegramLinkBodyProps) => {
  const intl = useIntl();
  const toast = useToast();
  const mounted = useRef(true);
  const linkAttempted = useRef(false);
  // The popup's button and the close button leave while linking; focus waits here.
  const statusRef = useRef<HTMLDivElement>(null);

  const report = (id: string, status: "success" | "error") =>
    toast({
      title: intl.formatMessage({ id }),
      status,
      position: "top",
      duration: 6000,
      isClosable: true,
    });

  // The popup outlives the dialog, and each opening is a new mount: a callback
  // from an earlier opening, or after closing, finds this false and does nothing.
  const isCurrent = () => mounted.current && isOpenNow.current;

  const { open, busy, notReady } = useTelegramOidcPopup({
    clientId,
    onToken: async ({ idToken }) => {
      if (!isCurrent()) return;
      linkAttempted.current = true;
      setIsLinking(true);
      await withSessionRefresh(() => telegramOidcLink(idToken, userId));
      setIsLinking(false);
      report("signInMethods.telegram.success", "success");
      finish(true);
    },
    onError: ({ phase, error }) => {
      if (!isCurrent()) return;
      if (phase === "token" && linkAttempted.current) {
        setIsLinking(false);
        report(telegramLinkErrorId(error), "error");
        // The server knows of a link this page does not show yet: catch up.
        finish(refusalOf(error).detail === "already_linked_self");
        return;
      }
      report(telegramPopupErrorId(phase, error), "error");
    },
  });

  useEffect(() => {
    mounted.current = true;
    // A failure here stays silent: the link call's own 401 handling covers it.
    const keepSessionFresh = () => {
      refreshSession().catch(() => undefined);
    };
    keepSessionFresh();
    // Bounded: each refresh rotates the refresh cookie, so an unbounded timer
    // would keep the session of an abandoned dialog alive forever. After the last
    // tick, the link call's own 401 handling still recovers a live session.
    let ticks = 0;
    const timer = window.setInterval(() => {
      keepSessionFresh();
      ticks += 1;
      if (ticks >= MAX_SESSION_REFRESH_TICKS) window.clearInterval(timer);
    }, NONCE_REFRESH_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (isLinking) statusRef.current?.focus();
  }, [isLinking]);

  if (isLinking) {
    return (
      <ModalBody pb={6}>
        <Flex ref={statusRef} role="status" tabIndex={-1} align="center" gap={2}>
          <Spinner size="sm" aria-hidden="true" />
          <Text>{intl.formatMessage({ id: "signInMethods.telegram.linking" })}</Text>
        </Flex>
      </ModalBody>
    );
  }

  return (
    <>
      <ModalCloseButton boxSize={TOUCH_TARGET} _focusVisible={FOCUS_RING} />
      <ModalBody>
        <Flex gap={3} p={3} borderRadius="md" bg="status.warningSubtle" color="text.primary">
          <Icon
            as={FaExclamationTriangle}
            boxSize={5}
            flexShrink={0}
            color="status.warningStrong"
            aria-hidden="true"
          />
          <Text>{intl.formatMessage({ id: "signInMethods.telegram.warning" })}</Text>
        </Flex>
      </ModalBody>
      <ModalFooter>
        {/* Opens the popup inside this click: no await may come before it. */}
        <BusyButton
          isBusy={busy || notReady}
          onClick={open}
          minH={TOUCH_TARGET}
          _focusVisible={FOCUS_RING}
        >
          {intl.formatMessage({ id: "signInMethods.telegram.open" })}
        </BusyButton>
      </ModalFooter>
    </>
  );
};

interface TelegramLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: number;
  userId: number;
  onLinked: () => void;
  /** The dialog has fully gone, exit animation included. */
  onClosed?: () => void;
}

/**
 * States the exposure, then opens Telegram's popup. A link never creates an
 * account, opens a session or changes the API key, so this path touches no
 * sign-in state and never falls back to the sign-in route, which on a taken
 * Telegram id would open the other account.
 */
const TelegramLinkModal = ({
  isOpen,
  onClose,
  clientId,
  userId,
  onLinked,
  onClosed,
}: TelegramLinkModalProps) => {
  const intl = useIntl();
  const [isLinking, setIsLinking] = useState(false);
  const isOpenNow = useRef(isOpen);
  isOpenNow.current = isOpen;
  // Told only once the dialog is gone: the card's refresh then moves focus,
  // which an open dialog's focus lock would pull straight back.
  const linkedOnClose = useRef(false);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={!isLinking}
      closeOnOverlayClick={!isLinking}
      onCloseComplete={() => {
        onClosed?.();
        if (!linkedOnClose.current) return;
        linkedOnClose.current = false;
        onLinked();
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent bg="bg.overlay" color="text.primary" mx={4}>
        <ModalHeader>{intl.formatMessage({ id: "signInMethods.telegram.title" })}</ModalHeader>
        <TelegramLinkBody
          clientId={clientId}
          userId={userId}
          isOpenNow={isOpenNow}
          isLinking={isLinking}
          setIsLinking={setIsLinking}
          finish={(linked) => {
            linkedOnClose.current = linked;
            onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
};

export default TelegramLinkModal;
